import { Fragment, type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  GUARDRAILS,
  type HubResponse,
  type HubScope,
  K_GOAL_LIFT,
  type PeriodMetrics,
} from "@/lib/consultant-hub";
import { cn } from "@/lib/utils";
import { fmtInt, fmtK, fmtPct } from "./shared";

type Verdict = "pass" | "fail" | null;

const tone = (v: Verdict) =>
  v === "pass" ? "text-emerald-600" : v === "fail" ? "text-rose-600" : "text-muted-foreground";
const cellTone = (v: Verdict) =>
  v === "pass"
    ? "bg-emerald-500/10 text-emerald-700"
    : v === "fail"
      ? "bg-rose-500/10 text-rose-700"
      : "";

const atLeast = (v: number | null, min: number): Verdict =>
  v === null ? null : v >= min ? "pass" : "fail";
const atMost = (v: number | null, max: number): Verdict =>
  v === null ? null : v <= max ? "pass" : "fail";

/** Consultants covering a scope: one per territory (none for deals with no state). */
function consultantCount(data: HubResponse, scope: HubScope): number | null {
  if (scope.kind === "territory") return 1;
  if (scope.kind === "division") return scope.territories?.length ?? null;
  if (scope.kind === "company") return data.scopes.filter((s) => s.kind === "territory").length;
  return null;
}

const kGoal = (m: PeriodMetrics) => (m.priorK === null ? null : m.priorK + K_GOAL_LIFT);

function Tile({
  label,
  value,
  target,
  verdict,
  children,
}: {
  label: string;
  value: string;
  target: string;
  verdict: Verdict;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        verdict === "pass" && "border-emerald-500/40",
        verdict === "fail" && "border-rose-500/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        {verdict && (
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-medium",
              verdict === "pass"
                ? "bg-emerald-500/10 text-emerald-700"
                : "bg-rose-500/10 text-rose-700",
            )}
          >
            {verdict === "pass" ? "On track" : "Off track"}
          </span>
        )}
      </div>
      <p className={cn("mt-1 text-2xl font-semibold tabular-nums", tone(verdict))}>{value}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{target}</p>
      {children}
    </div>
  );
}

/**
 * The four guardrails every territory is held to — gross profit margin, close
 * rate, opportunities per consultant per month, and K — for the year to date,
 * then period by period. K shows the numbers in its formula underneath.
 */
export function TerritoryGuardrails({
  data,
  scope,
  series,
  total,
  className,
}: {
  data: HubResponse;
  scope: HubScope;
  series: (PeriodMetrics | null)[];
  total: PeriodMetrics | null;
  className?: string;
}) {
  const consultants = consultantCount(data, scope);
  const cap = consultants ? GUARDRAILS.maxOppsPerConsultant : null;
  const perConsultant = (m: PeriodMetrics) =>
    consultants ? Math.round((m.oppsPerMonth / consultants) * 10) / 10 : null;

  const cols = data.periods
    .map((p, i) => ({ p, m: series[i] ?? null }))
    .filter((c) => c.p.status !== "future" && c.m);
  // Headline: the year to date (annual grain: the latest fiscal year shown).
  const head = total ?? cols[cols.length - 1]?.m ?? null;
  const headLabel = total ? `${data.fyLabel} year to date` : (cols[cols.length - 1]?.p.label ?? "");
  if (!head) return null;

  // Months (or periods) over the opportunity cap so far.
  const over = cap ? cols.filter((c) => c.m && (perConsultant(c.m) ?? 0) > cap) : [];
  const busiest = cols.reduce<{ label: string; v: number } | null>((best, c) => {
    const v = c.m ? (perConsultant(c.m) ?? 0) : 0;
    return !best || v > best.v ? { label: c.p.label, v } : best;
  }, null);
  const unit = data.grain === "month" ? "month" : "month (avg)";
  const p = head.kParts;

  // Period columns show that period's own K and counts; the year-to-date
  // column (and the tiles) show Fiscal YTD K and its running totals.
  const kOf = (m: PeriodMetrics, ytd: boolean) => (ytd ? m.k : m.periodK);
  const partsOf = (m: PeriodMetrics, ytd: boolean) => (ytd ? m.kParts : m.periodParts);
  const kVerdict = (m: PeriodMetrics, ytd = true): Verdict => {
    const g = kGoal(m);
    const k = kOf(m, ytd);
    return k === null || g === null ? null : k >= g ? "pass" : "fail";
  };
  const part =
    (key: "friend" | "affiliate" | "relational" | "lyClosed") =>
    (m: PeriodMetrics, ytd: boolean) => {
      const v = partsOf(m, ytd);
      return v ? fmtInt(v[key]) : "—";
    };

  const rows: {
    label: string;
    target: string;
    /** `ytd` is true for the year-to-date column. */
    value: (m: PeriodMetrics, ytd: boolean) => string;
    verdict?: (m: PeriodMetrics, ytd: boolean) => Verdict;
    sub?: boolean;
  }[] = [
    {
      label: "Gross profit margin",
      target: `≥ ${fmtPct(GUARDRAILS.minGpm)}`,
      value: (m) => fmtPct(m.gpm),
      verdict: (m) => atLeast(m.gpm, GUARDRAILS.minGpm),
    },
    {
      label: "Close rate",
      target: `≥ ${fmtPct(GUARDRAILS.minCloseRate)}`,
      value: (m) => fmtPct(m.closeRate),
      verdict: (m) => atLeast(m.closeRate, GUARDRAILS.minCloseRate),
    },
    ...(cap
      ? [
          {
            label: `Opportunities per consultant / ${unit}`,
            target: `≤ ${cap}`,
            value: (m: PeriodMetrics) => {
              const v = perConsultant(m);
              return v === null ? "—" : fmtInt(v);
            },
            verdict: (m: PeriodMetrics) => atMost(perConsultant(m), cap),
          },
        ]
      : []),
    {
      label: "K",
      target: `≥ last year + ${K_GOAL_LIFT.toFixed(2)}`,
      value: (m, ytd) => fmtK(kOf(m, ytd)),
      verdict: kVerdict,
    },
    { label: "K goal", target: "", value: (m) => fmtK(kGoal(m)), sub: true },
    {
      label: "Friends & family closes",
      target: "",
      value: part("friend"),
      sub: true,
    },
    {
      label: "+ Affiliate closes",
      target: "",
      value: part("affiliate"),
      sub: true,
    },
    {
      label: "= Total relational closes",
      target: "",
      value: part("relational"),
      sub: true,
    },
    {
      label: "÷ Total closed deals last year, same dates",
      target: "",
      value: part("lyClosed"),
      sub: true,
    },
  ];

  const td = "whitespace-nowrap px-3 py-1.5 text-right tabular-nums";

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          Guardrails <span className="font-normal text-muted-foreground">({headLabel})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Tile
            label="Gross profit margin"
            value={fmtPct(head.gpm)}
            target={`Minimum ${fmtPct(GUARDRAILS.minGpm)}`}
            verdict={atLeast(head.gpm, GUARDRAILS.minGpm)}
          />
          <Tile
            label="Close rate"
            value={fmtPct(head.closeRate)}
            target={`Minimum ${fmtPct(GUARDRAILS.minCloseRate)}`}
            verdict={atLeast(head.closeRate, GUARDRAILS.minCloseRate)}
          />
          {cap ? (
            <Tile
              label="Opps per consultant / month"
              value={fmtInt(perConsultant(head) ?? 0)}
              target={`Maximum ${cap} · average per completed month${consultants && consultants > 1 ? ` across ${consultants} consultants` : ""}`}
              verdict={over.length ? "fail" : "pass"}
            >
              <p className="mt-1 text-xs text-muted-foreground">
                {over.length
                  ? `Over the cap in ${over.map((c) => c.p.label).join(", ")}`
                  : busiest
                    ? `Busiest: ${busiest.label} (${fmtInt(busiest.v)})`
                    : ""}
              </p>
            </Tile>
          ) : (
            <Tile
              label="Opps per consultant / month"
              value="—"
              target="No consultant — these customers have no state on file"
              verdict={null}
            />
          )}
          <Tile
            label="Fiscal YTD K"
            value={fmtK(head.k)}
            target={`Goal ${fmtK(kGoal(head))} (last year's ${fmtK(head.priorK)} + ${K_GOAL_LIFT.toFixed(2)})`}
            verdict={kVerdict(head)}
          >
            {p && (
              <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                ({fmtInt(p.friend)} F&amp;F + {fmtInt(p.affiliate)} affiliate) ={" "}
                {fmtInt(p.relational)} ÷ {fmtInt(p.lyClosed)} last year
              </p>
            )}
          </Tile>
        </div>

        <div className="-mx-6 overflow-x-auto border-t">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="sticky left-0 z-10 min-w-64 bg-muted px-3 py-2 text-left font-medium text-muted-foreground">
                  Guardrail
                </th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground">Target</th>
                {cols.map((c) => (
                  <th
                    key={c.p.key}
                    className={cn(
                      "whitespace-nowrap px-3 py-2 text-right font-medium text-muted-foreground",
                      c.p.status === "current" && "text-foreground",
                    )}
                  >
                    {c.p.label}
                    {c.p.status === "current" && (
                      <div className="text-[10px] font-normal">to date</div>
                    )}
                  </th>
                ))}
                {total && (
                  <th className="whitespace-nowrap border-l px-3 py-2 text-right font-semibold">
                    Year to date
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.label}>
                  <tr className={cn("border-t border-border/50", !r.sub && "font-medium")}>
                    <td
                      className={cn(
                        "sticky left-0 z-10 bg-background px-3 py-1.5",
                        r.sub && "pl-8 text-muted-foreground",
                      )}
                    >
                      {r.label}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right text-muted-foreground">
                      {r.target}
                    </td>
                    {cols.map((c) => (
                      <td
                        key={c.p.key}
                        className={cn(td, c.m && r.verdict && cellTone(r.verdict(c.m, false)))}
                      >
                        {c.m ? r.value(c.m, false) : ""}
                      </td>
                    ))}
                    {total && (
                      <td
                        className={cn(
                          td,
                          "border-l font-semibold",
                          r.verdict && cellTone(r.verdict(total, true)),
                        )}
                      >
                        {r.value(total, true)}
                      </td>
                    )}
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Gross profit margin = (net − ring cost) ÷ gross, the same formula as commission. Close
          rate = closed deals ÷ opportunities in the same period, so the in-progress month runs low
          until its deals close. Opportunities count customers in this{" "}
          {scope.kind === "territory" ? "territory" : "area"}'s states
          {consultants && consultants > 1 ? `, split across its ${consultants} consultants` : ""}. K
          = (Friends &amp; family + Affiliate closes) ÷ all closes over the same dates last year.
          Each {data.grain === "year" ? "year" : data.grain} column shows only that{" "}
          {data.grain === "year" ? "year" : data.grain}'s own numbers
          {total ? "; the Year to date column adds them up since April 1 (Fiscal YTD K)" : ""}. The
          goal is last fiscal year's full-year K + {K_GOAL_LIFT.toFixed(2)}.
        </p>
      </CardContent>
    </Card>
  );
}
